import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {loadScript} from "../../../../utils/Networking";
import {selectUserAccount} from "../../../../stores/account/account.selectors";
import {useSelector} from "react-redux";
import {saveAccountDetailsAsync, updateAddressAsync} from "../../../../stores/account/account.thunks";
import {useDispatch} from "react-redux";
import {List} from "immutable";
import GlobalConst from "../../../../constants/GlobalConst";
import CCInfo from "../models/CCInfo";
import {v4} from "uuid";
import tradefanApi, {useGetProductsQuery} from "../../../../stores/tradefan/tradefan.api";
import {
    resetNuveiTransactionStatus,
    setNuveiTransactionStatus,
    setSessionId
} from "../../../../stores/ecommerce/ecommerce.slice";

export const ViewMode ={
    PRODUCT_SELECTION : 1,
    CUSTOMER_INFO_FORM : 2,
    TRANSACTION_PROCESSING: 3,
    TRANSACTION_COMPLETED: 4,
    TRANSACTION_FAILED: 5
};

export const steps = [
    ViewMode.PRODUCT_SELECTION,
    ViewMode.CUSTOMER_INFO_FORM,
];

const useNuveiViewModel = (props)=>{

    const dispatch = useDispatch();
    const [viewMode, setViewMode] = useState(ViewMode.PRODUCT_SELECTION);
    const submitting = useRef(false);
    const account = useSelector(selectUserAccount);
    const [cardN, setCardN] = useState(null);
    const [sessionToken, setSessionToken] = useState("");
    const [cardHolderName, setCardHolderName] = useState(account.firstName + " " + account.lastName);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [billingAddress, setBillingAddress] = useState(account.address);
    const [saveAsHomeAddress, setSaveAsHomeAddress] = useState(false);
    const [saveAccountDetails, setSaveAccountDetails] = useState(false);
    const [ccInfo, setCcInfo] = useState(new CCInfo({
        fname: account.firstName,
        lname: account.lastName,
        currency: GlobalConst.SITE_CURRENCY,
        mx_curp: "",
        zip: account.address.zipCode,
        city: account.address.city,
        address: account.address.address1,
        country: account.address.country,
        state: account.address.state,
        phone: account.address.phone1,
        isCustom: false,
        nuveiToken: "",
    }));
    const nuveiTransactionStatus = useSelector(state => state.ecommerce.nuvei.transactionStatus);
    const errors = useSelector(state => state.ecommerce.errors);
    const {data:products, error:productsError} = useGetProductsQuery();
    const safeCharge = useMemo(() => window.SafeCharge ? new window.SafeCharge({
        env: "int",
        merchantId: "7185477658065290218",
        merchantSiteId: "258898"
    }) : null, [window.SafeCharge]);

    useEffect(()=>{
        initStep(ViewMode.PRODUCT_SELECTION);
        submitting.current = false;
    },[]);

    useEffect(() => {
        if (safeCharge && viewMode === ViewMode.CUSTOMER_INFO_FORM) {
            const safeChargeFields = safeCharge.fields({
                fonts: [{ cssUrl: "" }],
                locale: "en",
                fontSize: "16px"
            });
            const style ={
                style: {
                    base: {
                        color: 'white'
                    }
                }
            };

            const cardNumber = safeChargeFields.create("ccNumber", style);
            cardNumber.attach("#card-number");

            const cardExpiry = safeChargeFields.create("ccExpiration", style);
            cardExpiry.attach("#card-expiry");

            const cardCvc = safeChargeFields.create("ccCvc", style);
            cardCvc.attach("#card-cvc");
            setCardN(cardNumber);
        }
    }, [safeCharge, viewMode]);

    useEffect(() => {
        if (nuveiTransactionStatus === "Approved") {
            goTo(ViewMode.TRANSACTION_COMPLETED, false);
        } else if (nuveiTransactionStatus === "Failed") {
            goTo(ViewMode.TRANSACTION_FAILED, false);
        }
    }, [nuveiTransactionStatus]);

    useEffect(() => {
        if(products)
            loadScript(
                "https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js" //production
            )
    }, [products]);



    const initStep = async (step)=>{
        switch (step){
            case ViewMode.PRODUCT_SELECTION:
                dispatch(resetNuveiTransactionStatus());
                break;
            case ViewMode.CUSTOMER_INFO_FORM:
                const res = await dispatch(tradefanApi.endpoints.initiateNuveiPayment.initiate({
                    Amount: selectedProduct.price,
                    Currency: selectedProduct.currency,
                    ClientUniqueId: v4(),
                    ClientRequestId: v4(),
                }))
                setSessionToken(res.data.SessionToken);
                break;
            case ViewMode.TRANSACTION_PROCESSING:

                break;
            case ViewMode.TRANSACTION_COMPLETED:

                break;
            case ViewMode.TRANSACTION_FAILED:

                //error = initData;

                break;
        }
    }

    const goTo = (view, shouldInit)=>{
        setViewMode( view );
        if (shouldInit)
            initStep( view);
    }
    const onProductSelected = (p)=>{
        setSelectedProduct(p);
    }

    const handleCustomerInfoSubmitClick = useCallback((e)=>{
        e.preventDefault();
        if(submitting.current)
            return;
        submitting.current = true;

        try {
            safeCharge.createPayment(
                {
                    sessionToken: sessionToken,
                    cardHolderName: ccInfo.fname + " " + ccInfo.lname,
                    paymentOption: cardN,
                    billingAddress: {
                        email: account.email,
                        country: account.address.countryCode
                    }
                },
                async (res) => {
                    if (res.transactionStatus === "APPROVED") {
                        dispatch(setNuveiTransactionStatus({transactionStatus:'Processing'}));
                        const addFundsRes = await dispatch(tradefanApi.endpoints.addFunds.initiate({
                            product: {...selectedProduct},
                            ccInfo: {...ccInfo, nuveiToken: sessionToken},
                            hostname: window.location.hostname === "localhost"
                                ? "pickx.tradefan" + GlobalConst.SITE_TLD
                                : window.location.hostname,
                        }));
                        dispatch(setSessionId(addFundsRes.data.SessionId));
                    } else {
                        dispatch(setNuveiTransactionStatus({
                            transactionStatus:'Failed',
                            error: res.errorDescription
                        }));
                    }
                }
            );
        } catch (e) {
            console.error(e);
        }

        if (saveAsHomeAddress)
            dispatch(updateAddressAsync({address: {
                    ...billingAddress,
                    addressId: account.address.addressId,
                },skipLastAccountUpdate: true}));
        if (saveAccountDetails)
            dispatch(saveAccountDetailsAsync({
                firstName: ccInfo.fname,
                lastName: ccInfo.lname,
                pseudonym: account.pseudonym,
                email: account.email
            }));
    },[ccInfo, safeCharge, sessionToken, selectedProduct, billingAddress, saveAsHomeAddress, saveAccountDetails]);

    const handleReturnToProdSelectionClick = ()=>{
        goTo(ViewMode.PRODUCT_SELECTION, true);
    }
    const handleOrderConfirmedOkClick = ()=>{
        dispatch(resetNuveiTransactionStatus());
        props.onClose();
        if(props.onCompleted)
            props.onCompleted();
    }
    const handleProductionSelectionSubmitClick = ()=>{
        goTo(ViewMode.CUSTOMER_INFO_FORM, true);
    }

    return {
        submitting: submitting.current,
        errors,
        viewMode,
        steps,
        safeCharge,
        sessionToken,
        products,
        saveAsHomeAddress, setSaveAsHomeAddress,
        saveAccountDetails, setSaveAccountDetails,
        selectedProduct, setSelectedProduct,
        ccInfo, setCcInfo,
        billingAddress, setBillingAddress,
        handleReturnToProdSelectionClick,
        handleOrderConfirmedOkClick,
        onProductSelected,
        handleProductionSelectionSubmitClick,
        handleCustomerInfoSubmitClick
    };
};
export default useNuveiViewModel;